import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";

import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, OutlinedInput, TextField } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";

import Title from "../../../common/Title";

import useAlert from "../../../../hooks/useAlert";
import useAuth from "../../../../hooks/useAuth";
import useAxios from "../../../../hooks/useAxios";
import { __createKosherReseller } from "../../../../api/sim";

const parseAuthorizedIps = (raw) => {
  if (!raw || !String(raw).trim()) return [];
  return String(raw)
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const TmobilejCreateSipCustomerForm = () => {
  const [formData, setFormData] = useState(null);
  const responseRef = useRef(null);
  const { setAlertMessage } = useAlert();
  const { user } = useAuth();
  const nav = useNavigate();

  const [response, call, loading] = useAxios({
    ...__createKosherReseller,
  });

  useEffect(() => {
    if (user && !user.admin) {
      nav("/");
    }
  }, [user, nav]);

  const initialData = {
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    sip_host: "",
    sip_port: "",
    sip_username: "",
    sip_password: "",
    authorized_ips_raw: "",
  };

  const validationSchema = yup.object({
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().required("Last name is required"),
    email: yup.string().email("Valid email required").required("Email is required"),
    phone_number: yup
      .string()
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
      .required("Phone number is required"),
    sip_host: yup.string().required("SIP host is required"),
    sip_port: yup
      .string()
      .required("SIP port is required")
      .test("port", "Must be a number between 1 and 65535", (v) => {
        const n = parseInt(v, 10);
        return !Number.isNaN(n) && n >= 1 && n <= 65535;
      }),
    sip_username: yup.string().nullable(),
    sip_password: yup.string().nullable(),
    authorized_ips_raw: yup.string().nullable(),
  });

  const formik = useFormik({
    initialValues: { ...initialData },
    validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    onSubmit: (values) => {
      const payload = {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        email: values.email.trim(),
        phone_number: values.phone_number.trim(),
        sip_host: values.sip_host.trim(),
        sip_port: parseInt(values.sip_port, 10),
        authorized_ips: parseAuthorizedIps(values.authorized_ips_raw),
      };
      const u = values.sip_username?.trim();
      const p = values.sip_password?.trim();
      if (u) payload.sip_username = u;
      if (p) payload.sip_password = p;
      setFormData(payload);
    },
  });

  useEffect(() => {
    if (formData) {
      call({ data: formData });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (
      responseRef.current &&
      responseRef.current.status === response.status &&
      JSON.stringify(responseRef.current.data) === JSON.stringify(response.data)
    ) {
      return;
    }
    responseRef.current = response;
    formik.setSubmitting(false);
    if (response.status) {
      setAlertMessage({
        type: "success",
        message: "SIP customer created successfully",
      });
      setFormData(null);
      nav("/tmobilej/activate");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  if (!user) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }
  if (!user.admin) {
    return null;
  }

  const requiredFieldsFilled =
    Boolean(formik.values.first_name?.trim()) &&
    Boolean(formik.values.last_name?.trim()) &&
    Boolean(formik.values.email?.trim()) &&
    Boolean(formik.values.phone_number?.trim()) &&
    Boolean(formik.values.sip_host?.trim()) &&
    Boolean(String(formik.values.sip_port ?? "").trim());

  const disableCreateButton =
    loading || formik.isSubmitting || !requiredFieldsFilled || !formik.isValid;

  return (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      alignItems="center"
      flexDirection="column"
    >
      <Title
        variant="h5"
        content="Create SIP customer (Kosher API)"
        sx={{ mb: 3 }}
        gutterBottom
      />
      <Grid
        container
        spacing={2}
        direction="column"
        justifyContent="center"
        alignItems="center"
        maxWidth={480}
      >
        {[
          ["first_name", "First name"],
          ["last_name", "Last name"],
          ["email", "Email"],
          ["phone_number", "Phone number"],
          ["sip_host", "SIP host"],
        ].map(([name, label]) => (
          <Grid item xs={12} key={name} sx={{ width: "100%" }}>
            <FormControl
              error={Boolean(formik.touched[name] && formik.errors[name])}
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor={name}>{label}</InputLabel>
              <OutlinedInput
                id={name}
                name={name}
                label={label}
                value={formik.values[name]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched[name] && formik.errors[name] ? (
                <FormHelperText>{formik.errors[name]}</FormHelperText>
              ) : null}
            </FormControl>
          </Grid>
        ))}

        <Grid item xs={12} sx={{ width: "100%" }}>
          <FormControl
            error={Boolean(formik.touched.sip_port && formik.errors.sip_port)}
            sx={{ width: "100%" }}
          >
            <InputLabel htmlFor="sip_port">SIP port</InputLabel>
            <OutlinedInput
              id="sip_port"
              name="sip_port"
              label="SIP port"
              type="number"
              value={formik.values.sip_port}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.sip_port && formik.errors.sip_port ? (
              <FormHelperText>{formik.errors.sip_port}</FormHelperText>
            ) : null}
          </FormControl>
        </Grid>

        {[
          ["sip_username", "SIP username (optional)"],
          ["sip_password", "SIP password (optional)"],
        ].map(([name, label]) => (
          <Grid item xs={12} key={name} sx={{ width: "100%" }}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel htmlFor={name}>{label}</InputLabel>
              <OutlinedInput
                id={name}
                name={name}
                label={label}
                type={name === "sip_password" ? "password" : "text"}
                value={formik.values[name]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </FormControl>
          </Grid>
        ))}

        <Grid item xs={12} sx={{ width: "100%" }}>
          <TextField
            fullWidth
            id="authorized_ips_raw"
            name="authorized_ips_raw"
            label="Authorized IPs (optional)"
            placeholder="213.196.28.2"
            multiline
            minRows={3}
            variant="outlined"
            value={formik.values.authorized_ips_raw}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText="One IP per line or comma-separated. Leave empty if there are no secondary IPs."
          />
        </Grid>

        <Grid item sx={{ mt: 2 }}>
          <LoadingButton
            variant="contained"
            loading={loading || formik.isSubmitting}
            disabled={disableCreateButton}
            onClick={() => formik.submitForm()}
          >
            Create customer
          </LoadingButton>
          <Button sx={{ ml: 1 }} onClick={() => nav("/tmobilej/activate")}>
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TmobilejCreateSipCustomerForm;
